// @ts-nocheck FIXME
import EventApi from '@legacy-components/apis/V5/EventApi';
import ArrayHelpers from '@legacy-components/components/ArrayHelpers';
import Utils from '@legacy-components/components/DynamicUtils';
import NavigationLink from '@legacy-components/react/NavigationLink';
import FormField from '@legacy-components/react/V5/Common/FormField';
import MultiSelect from '@legacy-components/react/V5/Common/MultiSelect/MultiSelectContainer';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React from 'react';

const SessionMaterialsCapsule = (props) => {
  // for convenience, list of non-deleted facilitators
  const facilitators = props.facilitators.filter((f) => !f.deleted);
  // if a session is cancelled or no show then everything is read-only
  const readOnly = !(
    props.status === 'Scheduled' || props.status === 'Delivered'
  );

  // by default, we hide the Co-Facilitators field, unless the session already has some
  const [coFacilitatorsVisible, setCoFacilitatorsVisible] = React.useState(
    facilitators.some((f) => f.role === 'CO')
  );
  // by default, the Event Materials capsule that holds the session materials links is collapsed
  const [isExpanded, setIsExpanded] = React.useState(false);

  React.useEffect(() => {
    // make sure this capsule is expanded to show any errors
    if (
      props.errors &&
      (props.errors.agendaLink ||
        props.errors.slideDeckLink ||
        props.errors.zoomLink ||
        props.errors.recordingLink)
    ) {
      setIsExpanded(true);
    }
  }, [props.errors]);

  const handleSelectFacilitator = (data, role) => {
    const newFacilitators = Utils.clone(props.facilitators);
    // look for this user in the list of selected facilitators
    const selectedOptionIndex = Utils.getItemIndexFromArrayOfObjects(
      newFacilitators,
      'id',
      data.id
    );
    const selectedOption =
      selectedOptionIndex !== -1 ? newFacilitators[selectedOptionIndex] : null;
    // if they are not in the list, add them
    if (selectedOptionIndex === -1) {
      const selectedFacilitator = props.facilitatorOptions.find(
        (facilitator) => facilitator.id === data.id
      );
      newFacilitators.push({
        label: `${selectedFacilitator.first_name} ${selectedFacilitator.last_name}`,
        role: role,
        ...selectedFacilitator,
      });
    }
    // if somehow they are in the list with a different role, change their role
    else if (selectedOption.role !== role) {
      selectedOption.role = role;
    }
    // if they are in the list and saved in the db, flip their "deleted" flag
    else if (selectedOption.event_session_facilitator_id) {
      selectedOption.deleted = !selectedOption.deleted;
    }
    // if they are in the list and not in the db yet, just remove them from the list
    else {
      newFacilitators.splice(selectedOptionIndex, 1);
    }
    // make sure the deleted facilitators are at the end of the list
    newFacilitators.sort((a, b) => !!a.deleted - !!b.deleted);
    props.update({ facilitators: newFacilitators });
  };

  const handleChangeModsFinalized = (e) => {
    const data = {
      modifications_finalized: e.target.checked
        ? moment().utc(true).format('ddd, DD MMM YYYY HH:mm:ss z')
        : null,
    };

    // if we are un-finalizing the modifications,
    // make sure the "materials finalized" checkbox is unchecked
    if (!e.target.checked && props.materials_finalized) {
      data.materials_finalized = null;
    }

    props.update(data);
  };

  // @todo move this function to the parent component and pass it down as a prop so this is testable.
  const deleteRecording = (id) => {
    EventApi.deleteEventSessionRecording({ recordingId: id })
      .then(() => {
        const newRecordings = props.event_session_recordings.filter(
          (recording) => recording.id !== id
        );
        props.update({ event_session_recordings: newRecordings });
        props.setModalOpen({});
      })
      .catch((response) => {
        props.setAlert({
          type: 'error',
          message: response.error,
          handleClose: () => props.setAlert({}),
        });
      });
  };

  const showDeleteRecordingModal = (id) => {
    props.setModalOpen({
      type: 'delete_event_session_recording',
      confirm: () => deleteRecording(id),
    });
  };

  let capsuleClass = props.greyBackground
    ? ' edit-event__session-capsule--slate'
    : '';
  if (props.isFirst) {
    capsuleClass += ' edit-event__session-capsule--no-margin';
  }
  // the Event Materials capsule is the opposite color of the surrounding capsule
  const materialsCapsuleClass = props.greyBackground
    ? ''
    : 'edit-event__session-mats-capsule--slate';

  // if there is only one session, we hide the heading row and the Name input
  let headingNode;
  if (!props.singleSession) {
    headingNode = (
      <div className="edit-event__session-capsule-headline-row v5__heading-5">
        <div className="tc-v5-book edit-event__row-start-icon"></div>
        <div className="edit-event__session-capsule-headline-text">
          {props.name}
        </div>
      </div>
    );
  }

  // if the session has a product code, display it
  let productCodeNode = (
    <div className="edit-event__session-capsule-mats-product-code empty">
      No product code set
    </div>
  );
  const productCode = props.ld_product_id
    ? props.allProductCodes[props.ld_product_id]
    : null;
  if (productCode) {
    let productNode = (
      <React.Fragment>
        <span className="bold">{productCode.product_code}:</span>{' '}
        {productCode.name}
      </React.Fragment>
    );
    // if the product code has an associated design doc, link to it
    if (productCode.design) {
      productNode = (
        <a
          href={productCode.design}
          target="_blank"
          rel="noopener noreferrer"
          className="v5__link"
        >
          <span className="bold">{productCode.product_code}:</span>{' '}
          {productCode.name}
        </a>
      );
    }

    // if the product code has a product expert, display their name
    const productExpertNode = productCode.product_expert ? (
      <div className="edit-event__session-capsule-mats-product-expert">
        <span className="bold">Product Expert:</span>{' '}
        {productCode.product_expert.first_name}{' '}
        {productCode.product_expert.last_name}
      </div>
    ) : null;

    productCodeNode = (
      <div className="edit-event__session-capsule-mats-product-code">
        {productNode}
        {productExpertNode}
      </div>
    );
  }

  // the icon in the Event Materials capsule header depends on
  // whether or not the event has modifications
  const materialsIconClass = props.eventHasModifications
    ? 'tc-v5-customized-info'
    : 'tc-v4-page';

  // show a green checkmark next to the Event Materials capsule header
  // when the session's materials are marked as "finalized"
  let checkmarkIconNode;
  if (props.materials_finalized) {
    checkmarkIconNode = (
      <span className="tc-icon_status-done edit-event__session-mats-capsule-checkmark">
        <span className="path1"></span>
        <span className="path2"></span>
      </span>
    );
  }

  // set up Lead Facilitator
  const leadFacilitator = facilitators.find((f) => f.role === 'LEAD');
  let leadFacilitatorNode = leadFacilitator ? (
    <div className="edit-event__fixed-value">{leadFacilitator.label}</div>
  ) : (
    <div className="edit-event__fixed-value empty">
      No lead facilitator assigned
    </div>
  );

  // set up Co-Facilitator(s)
  // unlike the other facilitator fields, if this one is empty we hide the whole row
  const coFacilitators = facilitators.filter((f) => f.role === 'CO');
  let coFacilitatorsNode = ArrayHelpers.hasElements(coFacilitators) ? (
    <div className="co-facilitators">
      <label className="edit-event__session-capsule-label">
        Co-Facilitator(s)
      </label>
      <div className="edit-event__fixed-value">
        {Utils.joinHTMLElementsOxfordStyle(coFacilitators.map((f) => f.label))}
      </div>
    </div>
  ) : null;

  // set up Tech Support
  const techFacilitators = facilitators.filter((f) => f.role === 'TECH');
  let techFacilitatorNode = ArrayHelpers.hasElements(techFacilitators) ? (
    <div className="edit-event__fixed-value">
      {Utils.joinHTMLElementsOxfordStyle(techFacilitators.map((f) => f.label))}
    </div>
  ) : (
    <div className="edit-event__fixed-value empty">
      No tech support assigned
    </div>
  );

  // if the user can change the session facilitators, show the checkbox and multiselects
  let facilitatorsCheckboxNode;
  if (!readOnly && props.canEditEventDetails) {
    // if any co-facilitators are selected,
    // disable the checkbox to hide the co-facilitators input
    let facilitatorsCheckboxProps = {};
    let facilitatorsCheckboxLabelClass = '';
    if (ArrayHelpers.hasElements(coFacilitators)) {
      facilitatorsCheckboxProps = { disabled: true };
      facilitatorsCheckboxLabelClass = ' disabled';
    }
    // set up checkbox to hide/show the Co-Facilitators multiselect
    facilitatorsCheckboxNode = (
      <div className="facilitators-checkbox">
        <input
          type="checkbox"
          id={`facilitators-checkbox-${props.index}`}
          defaultChecked={coFacilitatorsVisible}
          onChange={(e) => setCoFacilitatorsVisible(e.target.checked)}
          className="v5__checkbox edit-event__session-capsule-facilitators-checkbox"
          {...facilitatorsCheckboxProps}
        />
        <label
          className={`edit-event__session-capsule-facilitators-checkbox-label${facilitatorsCheckboxLabelClass}`}
          htmlFor={`facilitators-checkbox-${props.index}`}
        >
          Add co-facilitators
        </label>
      </div>
    );

    // set up the facilitator options
    const facilitatorOptions = props.facilitatorOptions.filter(
      (facilitator) => {
        // exclude any people that have been selected in any of the three fields
        return !facilitators.some((f) => f.id === facilitator.id);
      }
    );

    // build the multiselects
    leadFacilitatorNode = (
      <MultiSelect
        uId={`${props.groupNum ? 'group-' + props.groupNum : ''}session-${
          props.index
        }-lead-facilitator`}
        className="form-field form-field--no-margin"
        handleSelectOption={(data) => handleSelectFacilitator(data, 'LEAD')}
        selectedOptions={leadFacilitator ? [leadFacilitator] : []}
        dropdownOptions={facilitatorOptions}
        minCharCountForResults={0}
        stayOpenOnSelect={false}
        maximumSelectedOptionsCount={1}
        isSelectSearch={true}
        preventAutoFocus={true}
      />
    );
    coFacilitatorsNode = coFacilitatorsVisible ? (
      <div className="co-facilitators">
        <label className="edit-event__session-capsule-label">
          Co-Facilitator(s)
        </label>
        <MultiSelect
          uId={`${props.groupNum ? 'group-' + props.groupNum : ''}session-${
            props.index
          }-co-facilitators`}
          className="form-field form-field--no-margin"
          handleSelectOption={(data) => handleSelectFacilitator(data, 'CO')}
          selectedOptions={coFacilitators}
          dropdownOptions={facilitatorOptions}
          minCharCountForResults={0}
          stayOpenOnSelect={false}
          isSelectSearch={true}
          preventAutoFocus={true}
        />
      </div>
    ) : null;
    techFacilitatorNode = (
      <MultiSelect
        uId={`${props.groupNum ? 'group-' + props.groupNum : ''}session-${
          props.index
        }-tech-facilitator`}
        className="form-field form-field--no-margin"
        handleSelectOption={(data) => handleSelectFacilitator(data, 'TECH')}
        selectedOptions={techFacilitators}
        dropdownOptions={facilitatorOptions}
        minCharCountForResults={0}
        stayOpenOnSelect={false}
        isSelectSearch={true}
        preventAutoFocus={true}
      />
    );
  }

  // show the session materials form inputs if the capsule is expanded
  let arrowClass = '';
  let materialsCapsuleBodyNode;
  if (isExpanded) {
    arrowClass = 'edit-event__session-mats-capsule-arrow--up';
    // session materials are editable if the current user has broad editing permission,
    // or they are a facilitator for this session
    const canEditMaterials =
      !readOnly &&
      (props.canEditEventDetails ||
        facilitators.some((f) => f.id === props.user.id));

    const recordingsMsgNode = (
      <div className="v5__body-copy--italic">
        Recordings will be auto-uploaded when the Zoom session is completed.
      </div>
    );

    let recordingsLinkNode = null;
    if (
      props.event_session_recordings &&
      props.event_session_recordings.length
    ) {
      recordingsLinkNode = (
        <ul className="edit-event__session-recording-container">
          {props.event_session_recordings &&
            props.event_session_recordings.map((recording, i) => {
              let recordingLink = `Recording ${i + 1}`;
              // Link to the recordings page for converted and cleared recordings
              if ([2, 3].includes(recording.video_status)) {
                recordingLink = (
                  <NavigationLink
                    url={`/lab/event_session/recording/${recording.uuid}`}
                    className="tc__link"
                  >
                    {recordingLink}
                  </NavigationLink>
                );
              }
              // only show the delete icon if the user is allowed to edit
              let deleteIcon;
              if (canEditMaterials) {
                deleteIcon = (
                  <div
                    data-testid="delete-recording-icon"
                    className="v5__action-icon tc-delete"
                    onClick={() => showDeleteRecordingModal(recording.id)}
                  ></div>
                );
              }
              return (
                <li
                  key={`event-session-recording-${recording.id}`}
                  title={recording.video_link}
                >
                  <div className="edit-event__session-recording">
                    {recordingLink}
                    {deleteIcon}
                  </div>
                </li>
              );
            })}
        </ul>
      );
    }

    let disconnectNode = null;
    if (canEditMaterials) {
      disconnectNode = (
        <span
          className="v5__link edit-event__copy-link-text"
          onClick={() =>
            props.setModalOpen({
              type: 'disconnect_event_session_from_zoom',
              eventSessionId: props.id,
              sessionName: props.name,
              index: props.index,
            })
          }
        >
          Disconnect
        </span>
      );
    }

    // Set the default zoom node for disconnected sessions
    let zoomNode = (
      <React.Fragment>
        <div className="edit-event__session-mats-form-row">
          <label className="edit-event__session-capsule-label">
            Zoom Link (for virtual sessions)
            <div className="v5__comment-copy participant-v5__meeting-details-synced-text">
              <span className="tc-v5-sync green admin-v5_coach-dashboard-tool-icon" />
              <span className="participant-v5__synced-with-gcal-text">
                Synced with Zoom
              </span>
              {disconnectNode}
            </div>
            <div className="v5__body-copy--italic">
              Link will be auto-generated when session is saved with a date,
              start time, end time, and Lead Facilitator.
            </div>
          </label>
        </div>
        <div className="edit-event__session-mats-form-row">
          <label>
            Zoom Recording Link (for virtual sessions)
            {recordingsLinkNode || recordingsMsgNode}
          </label>
        </div>
      </React.Fragment>
    );

    // If the zoom_link is not null (legacy meetings) or zoom_join_link is an empty string (disconnected meetings)
    // Show the input boxes to edit link recordings.
    if (props.zoom_link !== null || props.zoom_join_link === '') {
      zoomNode = (
        <React.Fragment>
          <div className="edit-event__session-mats-form-row">
            <label className="edit-event__session-capsule-label">
              Zoom Link (for virtual sessions)
              <FormField
                type="text"
                disabled={!canEditMaterials}
                maxLength="200"
                value={props.zoom_link || ''}
                isControlled={true}
                onChange={(e) => props.update({ zoom_link: e.target.value })}
                error={props.errors && props.errors.zoomLink}
              />
            </label>
          </div>
          <div className="edit-event__session-mats-form-row">
            <label>
              Zoom Recording Link (for virtual sessions)
              {recordingsLinkNode}
              <div className="v5__body-copy--italic">
                For multiple links, press Return and paste each link on its own
                line.
              </div>
              <FormField
                type="textarea"
                disabled={!canEditMaterials}
                isControlled={true}
                value={props.recording_link || ''}
                onChange={(e) =>
                  props.update({ recording_link: e.target.value })
                }
                error={props.errors && props.errors.recordingLink}
              />
            </label>
          </div>
        </React.Fragment>
      );
      // Otherwise verify there is a zoom_meeting_id before showing the connected meeting UI
    } else if (props.zoom_meeting_id) {
      zoomNode = (
        <React.Fragment>
          <div className="edit-event__session-mats-form-row">
            <label className="edit-event__session-capsule-label">
              Zoom Link (for virtual sessions)
              <div className="v5__comment-copy participant-v5__meeting-details-synced-text">
                <span className="tc-v5-sync green admin-v5_coach-dashboard-tool-icon" />
                <span className="participant-v5__synced-with-gcal-text">
                  Synced with Zoom
                </span>
                {disconnectNode}
              </div>
              <div>
                <span className="bold">{props.zoom_join_link}</span>
                <span
                  className="v5__link edit-event__copy-link-text"
                  copytext={props.zoom_join_link}
                  onClick={props.handleCopy}
                >
                  Copy link
                </span>
              </div>
            </label>
          </div>
          <div className="edit-event__session-mats-form-row">
            <label>
              Zoom Recording Link (for virtual sessions)
              {recordingsLinkNode || recordingsMsgNode}
            </label>
          </div>
        </React.Fragment>
      );
    }

    // if the event has modifications, show the checkbox that the Product Expert will check
    // after they are done with their modifications
    let modsFinalizedCheckbox;
    let modsWarningNode;
    if (props.eventHasModifications) {
      // the current user can check the checkbox if they have broad editing permissions,
      // or if they are the product expert for the session's product code
      const canFinalizeMods =
        !readOnly &&
        (props.canEditEventDetails ||
          (productCode &&
            productCode.product_expert &&
            productCode.product_expert.id === props.user.id));

      // set up the "Modifications finalized" checkbox
      const modsFinalizedClass = !canFinalizeMods ? ' disabled' : '';
      modsFinalizedCheckbox = (
        <React.Fragment>
          <input
            type="checkbox"
            id={`session-mods-checkbox-${props.index}`}
            checked={!!props.modifications_finalized}
            onChange={handleChangeModsFinalized}
            disabled={!canFinalizeMods}
            className="v5__checkbox edit-event__session-mats-mods-checkbox"
          />
          <label
            className={`edit-event__session-mats-checkbox-label${modsFinalizedClass}`}
            htmlFor={`session-mods-checkbox-${props.index}`}
          >
            Modifications finalized
          </label>
        </React.Fragment>
      );

      // if the modifications have not been finalized, show a warning in the form
      if (!props.modifications_finalized) {
        modsWarningNode = (
          <div className="edit-event__session-mats-mods-warning">
            <span className="tc-v5-alert edit-event__session-mats-mods-warning-icon"></span>
            Modifications in progress
          </div>
        );
      }
    }

    // if the session materials still need modifications,
    // disable the "materials finalized" checkbox
    const matsFinalizedDisabled =
      !canEditMaterials ||
      (props.eventHasModifications && !props.modifications_finalized);
    const matsFinalizedClass = matsFinalizedDisabled ? ' disabled' : '';

    materialsCapsuleBodyNode = (
      <form className="edit-event__session-mats-form">
        <div className="edit-event__session-mats-form-row edit-event__session-mats-form-row--flex">
          <input
            type="checkbox"
            id={`session-materials-checkbox-${props.index}`}
            checked={!!props.materials_finalized}
            onChange={(e) =>
              props.update({
                materials_finalized: e.target.checked
                  ? moment().utc(true).format('ddd, DD MMM YYYY HH:mm:ss z')
                  : null,
              })
            }
            disabled={matsFinalizedDisabled}
            className="v5__checkbox"
          />
          <label
            className={`edit-event__session-mats-checkbox-label${matsFinalizedClass}`}
            htmlFor={`session-materials-checkbox-${props.index}`}
          >
            Event materials finalized
          </label>
          {modsFinalizedCheckbox}
        </div>
        <div className="edit-event__session-mats-form-row">
          <label className="edit-event__session-capsule-label">
            Agenda Link
            {modsWarningNode}
            <FormField
              type="text"
              disabled={!canEditMaterials}
              maxLength="200"
              value={props.agenda_link || ''}
              isControlled={true}
              onChange={(e) => props.update({ agenda_link: e.target.value })}
              error={props.errors && props.errors.agendaLink}
              data-testid="session-agenda-link-input"
            />
          </label>
        </div>
        <div className="edit-event__session-mats-form-row">
          <label className="edit-event__session-capsule-label">
            Slide Deck Link
            {modsWarningNode}
            <FormField
              type="text"
              disabled={!canEditMaterials}
              maxLength="200"
              value={props.slide_deck_link || ''}
              isControlled={true}
              onChange={(e) =>
                props.update({ slide_deck_link: e.target.value })
              }
              error={props.errors && props.errors.slideDeckLink}
            />
          </label>
        </div>
        {zoomNode}
      </form>
    );
  }

  return (
    <div className={`edit-event__session-capsule${capsuleClass}`}>
      {headingNode}

      {productCodeNode}

      <div className="edit-event__session-capsule-form edit-event__session-capsule-form--mats v5__body-copy">
        <div className="lead-facilitator">
          <label className="edit-event__session-capsule-label">
            Lead Facilitator
          </label>
          {leadFacilitatorNode}
        </div>

        {facilitatorsCheckboxNode}

        {coFacilitatorsNode}

        <div className="tech-facilitator">
          <label className="edit-event__session-capsule-label">
            Tech Support
          </label>
          {techFacilitatorNode}
        </div>

        <div
          className={`edit-event__session-mats-capsule ${materialsCapsuleClass}`}
        >
          <div
            onClick={() => setIsExpanded(!isExpanded)}
            data-testid="session-materials-capsule"
            className="edit-event__session-mats-capsule-headline-row v5__heading-5"
          >
            <div className={`edit-event__row-start-icon ${materialsIconClass}`}>
              <span className="path1"></span>
              <span className="path2"></span>
              <span className="path3"></span>
              <span className="path4"></span>
              <span className="path5"></span>
            </div>
            <div className="edit-event__session-mats-capsule-headline">
              <span className="edit-event__session-mats-capsule-headline-text">
                Event Materials
              </span>
              {checkmarkIconNode}
            </div>
            <div
              data-testid={`session-mats-capsule-arrow-${props.id}`}
              className={`tc-v4-circle-arrow edit-event__session-mats-capsule-arrow ${arrowClass}`}
            ></div>
          </div>
          {materialsCapsuleBodyNode}
        </div>
      </div>
    </div>
  );
};

SessionMaterialsCapsule.propTypes = {
  ld_product_id: PropTypes.number,
  allProductCodes: PropTypes.object.isRequired,
  facilitatorOptions: PropTypes.array.isRequired,
  facilitators: PropTypes.array.isRequired,
  handleCopy: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  setAlert: PropTypes.func.isRequired,
  setModalOpen: PropTypes.func.isRequired,
  update: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  eventHasModifications: PropTypes.bool,
  canEditEventDetails: PropTypes.bool,
  errors: PropTypes.object,
  greyBackground: PropTypes.bool,
  materials_finalized: PropTypes.string,
  modifications_finalized: PropTypes.string,
  name: PropTypes.string,
  num: PropTypes.number,
  singleSession: PropTypes.bool,
};

SessionMaterialsCapsule.defaultProps = {
  facilitators: [],
};

export default SessionMaterialsCapsule;
